import React, { useRef, useState } from 'react';
import { Button, Checkbox, CheckboxProps, Input, InputRef, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ProColumns } from '@ant-design/pro-components';
import { FilterDropdownProps } from 'antd/es/table/interface';
import { FaFilter } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { WasteHandoverPlanModel } from 'services/Main/wasteHandoverPlan_services';
import { normalizeValue, isEmptyValue } from '../utils/valueHelpers';

/**
 * Hook để tạo filter dropdown cho các cột trong table
 * @param displayData Dữ liệu hiển thị trong table
 * @returns Object chứa hàm getColumnSearchProps
 */
export const useColumnFilters = (displayData: WasteHandoverPlanModel[]) => {
  const { t } = useTranslation();
  const searchInput = useRef<InputRef>(null);
  const [searchText, setSearchText] = useState<Record<string, string>>({});

  /**
   * Xử lý khi người dùng xác nhận filter
   */
  const handleFilterConfirm = (selectedKeys: string[], confirm: FilterDropdownProps['confirm']) => {
    confirm();
  };

  /**
   * Xử lý khi người dùng reset filter
   */
  const handleFilterReset = (
    clearFilters: (() => void) | undefined,
    confirm: FilterDropdownProps['confirm'],
    dataIndex: keyof WasteHandoverPlanModel
  ) => {
    if (clearFilters) {
      clearFilters();
    }
    confirm();
    setSearchText((prev) => ({ ...prev, [dataIndex]: '' }));
  };

  /**
   * Tạo props cho column filter dropdown
   * @param dataIndex Tên field cần filter
   * @param type Loại filter ('date' hoặc undefined)
   * @param filterCondition Điều kiện filter dữ liệu trước khi lấy unique values (ví dụ: (record) => record.actived === false)
   * @returns ProColumns với filter dropdown
   */
  const getColumnSearchProps = (
    dataIndex: keyof WasteHandoverPlanModel,
    type?: 'date',
    filterCondition?: (record: WasteHandoverPlanModel) => boolean
  ): ProColumns<WasteHandoverPlanModel> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      // Lọc dữ liệu theo điều kiện nếu có (để lấy unique values)
      const filteredData = filterCondition
        ? displayData.filter(filterCondition)
        : displayData;

      // Lấy các giá trị từ filteredData
      const filteredDataValues = filteredData.map((data) => normalizeValue(data[dataIndex], type));
      const filteredUniqueValues = Array.from(new Set(filteredDataValues));

      // Kiểm tra xem có giá trị trống trong toàn bộ displayData không
      // Nếu có, thêm "-" vào danh sách (ngay cả khi không có trong filteredData)
      const hasEmptyInAllData = displayData.some((data) => isEmptyValue(data[dataIndex]));
      const finalUniqueValues = hasEmptyInAllData && !filteredUniqueValues.includes('-')
        ? [...filteredUniqueValues, '-']
        : filteredUniqueValues;

      // Lọc theo keyword tìm kiếm
      const keyword = (searchText[dataIndex] || '').toLowerCase();
      const searchFilteredValues = finalUniqueValues.filter((item) => {
        if (!keyword) return true;
        // Cho phép search "-" để tìm giá trị trống
        if (item === '-') {
          return keyword === '-' || keyword === 'trống' || keyword === 'empty';
        }
        return item.toLowerCase().includes(keyword);
      });

      const selectedValues = selectedKeys as string[];
      const checkedAll = searchFilteredValues.length > 0 && searchFilteredValues.length === selectedValues.length;
      const indeterminate = selectedValues.length > 0 && selectedValues.length < searchFilteredValues.length;

      const onCheckAllChange: CheckboxProps['onChange'] = (e) => {
        setSelectedKeys(e.target.checked ? searchFilteredValues : []);
      };

      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={t('btn.search')}
            value={searchText[dataIndex] || ''}
            onChange={(e) => setSearchText((prev) => ({ ...prev, [dataIndex]: e.target.value }))}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <hr className="mb-2 mt-1" />
          <Space direction="vertical" className="max-h-[250px] overflow-y-auto w-full">
            <Checkbox checked={checkedAll} indeterminate={indeterminate} onChange={onCheckAllChange}>
              {t('btn.select-all')}
            </Checkbox>
            <Checkbox.Group
              value={selectedValues}
              onChange={(checked) => setSelectedKeys(checked as string[])}
            >
              <Space direction="vertical">
                {searchFilteredValues.map((item) => (
                  <Checkbox key={item} value={item}>
                    {item}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          </Space>
          <hr className="mb-2 mt-1" />
          <Space>
            <Button
              type="primary"
              htmlType="button"
              onClick={() => handleFilterConfirm(selectedValues, confirm)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              {t('btn.search')}
            </Button>
            <Button
              onClick={() => handleFilterReset(clearFilters, confirm, dataIndex)}
              size="small"
              style={{ width: 90 }}
            >
              {t('btn.reset')}
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => <FaFilter style={{ color: filtered ? '#1677ff' : undefined }} />,
    onFilter: (value, record) => {
      // Xử lý filter cho giá trị trống (khi value là "-")
      if (value === '-') {
        // Khi filter giá trị trống, chỉ kiểm tra giá trị trống, không áp dụng filterCondition
        // Vì filterCondition chỉ dùng để giới hạn danh sách options trong dropdown
        return isEmptyValue(record[dataIndex]);
      }

      // Xử lý filter cho giá trị không trống
      // Áp dụng điều kiện filter nếu có (cho các giá trị không phải trống)
      if (filterCondition && !filterCondition(record)) {
        return false;
      }

      const recordValue = normalizeValue(record[dataIndex], type);
      // So sánh chính xác (không phải includes) để đảm bảo filter chính xác
      return recordValue.toLowerCase() === (value as string).toLowerCase();
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  return { getColumnSearchProps };
};

